/**
 * Grossflaechiges Overlay ueber dem Chat-Container, das die aktive Animation
 * (searching / creating / success) zeigt und im idle-State unsichtbar wird.
 *
 * Render-Anforderung: muss in einem Container mit `position: relative`
 * sitzen, denn das Overlay positioniert sich absolut darueber.
 *
 * Assets: animiertes WebP mit Alpha-Channel pro State. Pfad-Konvention
 * <stem>-<state>.webp aus der mascotUrl abgeleitet (mascotUrl muss auf
 * .png enden). Animated WebP laeuft in <img> automatisch — kein play()/
 * pause(), kein <video>-Boilerplate.
 *
 * Wenn keine mascotUrl gesetzt ist oder der Stem nicht auf .png endet,
 * rendert die Komponente nichts.
 */
import { useState } from 'react';
import { useTenantSettings } from '../lib/useTenantSettings';
import { useAssistantState, type AssistantState } from '../context/AssistantStateContext';

const VIDEO_STATES: ReadonlyArray<Exclude<AssistantState, 'idle'>> = ['searching', 'creating', 'success'];
// SD-2026-00031 (Onvice, 2026-07-31): Extensions in Preferenz-Reihenfolge.
// Der Overlay probiert pro State BEIDE URLs — die erste die laedt gewinnt,
// die andere failed onError und wird ausgeblendet. Damit koennen Tenants
// entweder .webp (animiert, mit Alpha, wie Meiners) oder .svg (SMIL-
// Animation, wie Onvice) liefern, ohne Config-Aenderung.
const EXTENSIONS = ['svg', 'webp'] as const;

interface Props {
  /** Sichtbarkeit der Animation in idle: 0 (unsichtbar) bis 1. Default 0. */
  idleOpacity?: number;
  /** Sichtbarkeit waehrend einer aktiven Animation: 0 bis 1. Default 1.0. */
  activeOpacity?: number;
  /** Maximalbreite des Animations-Quadrats in px. Default 120. */
  maxSize?: number;
}

export default function AssistantStateOverlay({
  idleOpacity = 0,
  activeOpacity = 1.0,
  maxSize = 120,
}: Props = {}) {
  const { assistantMascotUrl } = useTenantSettings();
  const { state } = useAssistantState();
  // SD-2026-00031 (Onvice, 2026-07-31): Wenn ein Tenant nur das idle-PNG
  // hat aber keine animierten <stem>-<state>.webp-Files (z.B. Onvice mit
  // /assets/onvice-mascot.png), lud der Overlay 404 und zeigte einen leeren
  // Platzhalter bei aktivem State. onError merkt sich failed states.
  const [failed, setFailed] = useState<Set<string>>(() => new Set());

  if (!assistantMascotUrl) return null;
  const stem = assistantMascotUrl.replace(/\.png$/i, '');
  if (stem === assistantMascotUrl) return null;

  const visible = state !== 'idle';
  // Wenn ALLE States UND alle Extensions failed sind: gar nicht rendern.
  const allFailed = VIDEO_STATES.every((s) => EXTENSIONS.every((ext) => failed.has(`${s}.${ext}`)));
  if (allFailed) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        display: 'grid',
        placeItems: 'center',
        opacity: visible ? activeOpacity : idleOpacity,
        transition: 'opacity 0.6s ease-in-out',
        zIndex: 5,
      }}
    >
      <div style={{ position: 'relative', width: '60%', maxWidth: maxSize, aspectRatio: '1' }}>
        {VIDEO_STATES.flatMap((s) =>
          EXTENSIONS.map((ext) => {
            const key = `${s}.${ext}`;
            return (
              <img
                key={key}
                src={`${stem}-${s}.${ext}`}
                alt=""
                onError={() => setFailed((prev) => {
                  if (prev.has(key)) return prev;
                  const next = new Set(prev);
                  next.add(key);
                  return next;
                })}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  // failed = nicht rendern (verhindert leeren Platzhalter).
                  display: failed.has(key) ? 'none' : 'block',
                  opacity: state === s ? 1 : 0,
                  transition: 'opacity 0.4s ease-in-out',
                }}
              />
            );
          }),
        )}
      </div>
    </div>
  );
}
